import { isOnHlxHost } from '~/config/branding';
import { paths } from './routes';

export const RETURN_TO_PARAM = 'return_to';
export const CLERK_SATELLITE_URL_PARAM = '__clerk_satellite_url';
export const CLERK_SYNC_PARAM = '__clerk_synced';

/**
 * [MCV-1666] Carries the role chosen on `LoginOrCreateAccountPage` through to
 * the combined `<SignIn withSignUp>` card as a query param, e.g.
 * `/sign-in?role=provider`. `SignInPage` maps a valid value to
 * `setPendingRole` so a roleless account created on the resulting Clerk
 * signup can be resolved without asking again.
 */
export const ROLE_INTENT_PARAM = 'role';

// [MCV-1588] `/employer-user-invitation/` is here so a colleague invitation can
// survive sign-up. It is the one authenticated destination a signed-OUT person
// is routinely sent to by email, and without it `sanitizeReturnTo` nulls the
// link and the invitee is dropped into new-organization registration instead.
//
// Widening this list is safe in a way it would not have been before MCV-1582:
// these prefixes now only decide whether a path is worth PRESERVING, and
// `postAuthRedirect` independently validates the result against the mounted
// route table, bouncing anything unregistered to a dashboard. A typo'd
// `/employer-user-invitation/xyz` therefore cannot become a same-path redirect
// cycle.
const APP_PATH_PREFIXES = ['/vault/', '/hlx/', '/register/', '/employer-user-invitation/'];

/**
 * Normalizes a raw return_to value against the current origin and returns the
 * path+search+hash if it is same-origin and looks like an in-app destination.
 * Uses the URL constructor to defeat protocol-relative (`//evil.com`) and
 * backslash tricks; the explicit prefix check keeps redirects scoped to app
 * pages a signed-out user could reasonably have been trying to reach.
 */
export const sanitizeReturnTo = (raw: string | null | undefined, origin: string): string | null => {
  if (!raw) return null;
  if (/[\s\r\n]/.test(raw)) return null;

  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }

  if (url.origin !== origin) return null;

  const path = `${url.pathname}${url.search}${url.hash}`;
  if (!isAppPath(url.pathname)) {
    return null;
  }

  return path;
};

export const isProviderAssociationPath = (path: string | null): path is string => {
  if (!path) return false;
  try {
    return new URL(path, 'https://app.local').pathname === paths.providerAssociation.verify;
  } catch {
    return false;
  }
};

export const isAppPath = (pathname: string): boolean =>
  pathname === paths.providerAssociation.verify ||
  APP_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

export const sanitizeSatelliteReturnUrl = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  if (/[\s\r\n]/.test(raw)) return null;

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  if (url.protocol !== 'https:') return null;
  if (!isOnHlxHost(url.hostname)) return null;

  if (url.pathname === '/') {
    url.pathname = paths.hlx.dashboard;
    return url.toString();
  }

  if (url.pathname !== paths.publicAuth.authComplete && !isAppPath(url.pathname)) return null;

  return url.toString();
};

export const buildSignInPathWithReturnTo = (pathname: string, search: string, hash: string) => {
  const target = `${pathname}${search}${hash}`;
  const params = new URLSearchParams({ [RETURN_TO_PARAM]: target });
  return `${paths.publicAuth.signIn}?${params.toString()}`;
};

export const buildAuthCompletePath = (returnTo?: string | null): string => {
  if (!returnTo) return paths.publicAuth.authComplete;

  const params = new URLSearchParams({ [RETURN_TO_PARAM]: returnTo });
  return `${paths.publicAuth.authComplete}?${params.toString()}`;
};

export const buildSatelliteAuthCompleteUrl = (origin: string, returnTo?: string | null): string => {
  const url = new URL(buildAuthCompletePath(returnTo), origin);
  return url.toString();
};

/**
 * Stamps Clerk's `__clerk_synced` marker on a satellite URL.
 *
 * By default it writes `false`, asking the satellite to sync with the primary
 * before rendering. [MCV-1719] `skip` writes `true` instead, telling it there
 * is nothing to sync; only for a visitor with no primary session to carry.
 */
export const withClerkSatelliteSync = (
  rawUrl: string,
  { skip = false }: { skip?: boolean } = {}
): string => {
  const url = new URL(rawUrl, window.location.origin);
  url.searchParams.set(CLERK_SYNC_PARAM, skip ? 'true' : 'false');
  return url.toString();
};

/**
 * Normalize Clerk's satellite carrier without wrapping an existing auth-complete URL.
 *
 * The result carries `__clerk_synced=false` (sync with the primary before
 * rendering) unless `synced` is set. [MCV-1789] `synced: true` is for the caller
 * that is itself the signed-in primary completing the hand-off: it stamps `true`,
 * telling the satellite its session has been delivered and it must not hop back.
 */
export const resolveSatelliteAuthCompleteUrl = (
  raw: string | null | undefined,
  { synced = false }: { synced?: boolean } = {}
): string | null => {
  const satelliteReturn = sanitizeSatelliteReturnUrl(raw);
  if (!satelliteReturn) return null;

  const url = new URL(satelliteReturn);
  const target =
    url.pathname === paths.publicAuth.authComplete
      ? url.toString()
      : buildSatelliteAuthCompleteUrl(url.origin, `${url.pathname}${url.search}${url.hash}`);
  return withClerkSatelliteSync(target, { skip: synced });
};

export const CLERK_REDIRECT_URL_PARAM = 'redirect_url';
export const OAUTH_COMPLETION_URL_PARAM = 'oauth_completion_url';

/**
 * [MCV-1591] Reads Clerk's preserved `redirect_url` from EITHER carrier.
 *
 * Clerk emits it in the fragment (`#/?redirect_url=...`) as well as the query,
 * and only a mounted Clerk path router merges the fragment back into the
 * query. The role chooser mounts no Clerk component at all, so a query-only
 * reader silently misses the form Clerk actually produces.
 */
export const readAuthHandoffUrl = (search: string, hash: string): string | null => {
  const fromQuery = new URLSearchParams(search).get(CLERK_REDIRECT_URL_PARAM);
  if (fromQuery) return fromQuery;

  const queryInHash = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
  return new URLSearchParams(queryInHash).get(CLERK_REDIRECT_URL_PARAM);
};

/**
 * [MCV-1591] Accepts only the one shape a real satellite hand-off produces: an
 * `/auth/complete` URL on this origin or on an HTTPS HLX host.
 *
 * `sanitizeSatelliteReturnUrl` cannot be reused here -- `/auth/complete` is not
 * an APP_PATH_PREFIX, and widening that list would change what
 * `postAuthRedirect` preserves too. This mirrors its shape and reuses the same
 * host check, so the chooser cannot become an open redirect.
 */
export const sanitizeAuthHandoffUrl = (
  raw: string | null | undefined,
  origin: string
): string | null => {
  if (!raw) return null;
  if (/[\s\r\n]/.test(raw)) return null;

  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }

  if (url.pathname !== paths.publicAuth.authComplete) return null;

  const sameOrigin = url.origin === origin;
  const trustedHlx = url.protocol === 'https:' && isOnHlxHost(url.hostname);
  if (!sameOrigin && !trustedHlx) return null;

  return url.toString();
};

/**
 * Validates the completion URL carried through a Core 3 custom OAuth callback.
 *
 * Normal OAuth success goes directly to `SignIn.sso()`/`SignUp.sso()`'s
 * `redirectUrl`. Clerk only uses `redirectCallbackUrl` when authentication
 * needs another step, so that callback must carry the intended completion URL
 * into the continuation UI. Keep same-origin app destinations relative and
 * allow an absolute URL only for the existing, tightly-scoped HLX
 * `/auth/complete` satellite hand-off.
 */
export const sanitizeOAuthCompletionUrl = (
  raw: string | null | undefined,
  origin: string
): string | null => {
  if (!raw || /[\s\r\n]/.test(raw)) return null;

  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }

  if (url.origin === origin) {
    const path = `${url.pathname}${url.search}${url.hash}`;
    if (
      url.pathname === paths.landing ||
      url.pathname === paths.publicAuth.authComplete ||
      isAppPath(url.pathname)
    ) {
      return path;
    }
    return null;
  }

  if (
    url.protocol === 'https:' &&
    isOnHlxHost(url.hostname) &&
    url.pathname === paths.publicAuth.authComplete
  ) {
    return url.toString();
  }

  return null;
};
